import { CheckCircleFilled } from '@ant-design/icons';
import { Empty, Skeleton, Space, Tag, Timeline, Typography } from 'antd';
import type { QuestionHistory } from '@titularis/api-client';
import {
  formatarCusto,
  formatarData,
  MODULO_ASSISTIDO_LABEL,
  MOTOR_ASSISTIDO_LABEL,
} from '../dominio';

const ESTADO_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  suggestion: 'Sugestão da máquina',
  published_snapshot: 'Publicada',
};

/*
 * Qual módulo produziu, e por qual motor - com o nome vindo do `dominio`, que é
 * onde a lista de questões também o lê. Enquanto cada tela tinha a sua cópia, o
 * mesmo run era "Cadastro Assistido" numa e outra coisa na outra.
 */

/**
 * "por Fulano" quando há autor. Se o id existe mas o nome não, a conta foi
 * excluída (a autoria é preservada, mas sem nome): a linha mostra
 * "por Conta excluída". Sem id, nada.
 */
const porAutor = (id: string | null, nome: string | null): string => {
  if (!id) return '';
  return ` por ${nome ?? 'Conta excluída'}`;
};

/**
 * Histórico de versões.
 *
 * Nada é apagado: cada edição vira um ponto na linha, com o Código de
 * Versionamento e a data. A versão vigente é a que o aluno enxerga e nem sempre
 * é a mais recente - editar não publica.
 */
export function HistoricoVersoes({
  versoes,
  versaoVigenteId,
  carregando,
}: {
  versoes: QuestionHistory;
  versaoVigenteId: string | null;
  carregando: boolean;
}) {
  if (carregando) return <Skeleton active paragraph={{ rows: 3 }} />;

  if (versoes.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma versão ainda." />;
  }

  return (
    <Timeline
      items={versoes.map((versao) => {
        const ehVigente = versao.id === versaoVigenteId;

        return {
          key: versao.id,
          color: ehVigente ? 'green' : 'gray',
          icon: ehVigente ? <CheckCircleFilled style={{ fontSize: 14 }} /> : undefined,
          content: (
            <Space orientation="vertical" size={2} style={{ width: '100%' }}>
              <Space size={6} wrap>
                <Typography.Text code>{versao.versionCode}</Typography.Text>
                {ehVigente ? (
                  <Tag color="green">Vigente para o aluno</Tag>
                ) : (
                  <Tag>{ESTADO_LABEL[versao.state] ?? versao.state}</Tag>
                )}
              </Space>
              {/*
                A máquina não é usuário (D-17): a versão da pipeline não tem
                autor, e dizer "por Fulano" atribuiria a alguém um texto que ele
                não escreveu. A linha separa os dois fatos - quem acionou o run
                e qual modelo produziu o conteúdo.
              */}
              {versao.ingestionRunId ? (
                <Typography.Text type="secondary">
                  Sugerida em {formatarData(versao.createdAt)} pelo{' '}
                  {versao.ingestionKind
                    ? MODULO_ASSISTIDO_LABEL[versao.ingestionKind]
                    : 'Cadastro Assistido'}
                  {versao.ingestionEngine
                    ? ` (${MOTOR_ASSISTIDO_LABEL[versao.ingestionEngine]})`
                    : ''}
                  {versao.triggeredByName ? `, a pedido de ${versao.triggeredByName}` : ''}
                  {versao.ingestionModel ? ` · ${versao.ingestionModel}` : ''}
                  {formatarCusto(versao.ingestionCostUsd)
                    ? ` · ${formatarCusto(versao.ingestionCostUsd)}`
                    : ''}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">
                  Salva em {formatarData(versao.createdAt)}
                  {porAutor(versao.authorId, versao.authorName)}
                </Typography.Text>
              )}
              {/*
                A observação que o motor deixou sobre aquele trabalho (D-41).

                **Recolhida por padrão, e é essa a decisão.** Ela existe para o
                caso em que a sugestão parece rasa e não é - "o acervo não tem
                julgado sobre o tema, a aba saiu como complemento" -, mas a
                maioria das versões não tem nota, e as que têm não precisam
                gritar: o histórico é uma linha do tempo, não um mural. Duas
                linhas dizem se vale abrir; `collapsible` devolve o tamanho
                depois, porque são até 4000 caracteres e um texto que só abre
                empurraria o resto da linha do tempo para baixo sem volta.
              */}
              {versao.ingestionNote ? (
                <div>
                  {/*
                    O rótulo fica **fora** do `Paragraph`: o `ellipsis` do
                    Typography mede texto, e filho que é elemento React faz o
                    corte errar sem que o Ant reclame. É o mesmo cuidado da
                    lista de trabalho.
                  */}
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    Nota do motor
                  </Typography.Text>
                  <Typography.Paragraph
                    type="secondary"
                    style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                    ellipsis={{
                      rows: 2,
                      expandable: 'collapsible',
                      symbol: (expandido) => (expandido ? 'ver menos' : 'ver mais'),
                    }}
                  >
                    {versao.ingestionNote}
                  </Typography.Paragraph>
                </div>
              ) : null}
              {versao.publishedAt ? (
                <Typography.Text type="secondary">
                  Publicada em {formatarData(versao.publishedAt)}
                  {porAutor(versao.publishedBy, versao.publishedByName)}
                </Typography.Text>
              ) : null}
            </Space>
          ),
        };
      })}
    />
  );
}
